/**
 * Las condiciones de un público (SPEC-BD §6.4, B-16). Siempre «Y»: entran los que cumplen TODAS. Un
 * constructor con Y/O mezclados es donde el operador se equivoca; para un «O» entre etiquetas está
 * «incluye alguna de».
 */

import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import type { CondicionPublico, FiltroPublico, OpcionesBase, TipoCondicion } from "../../types.base";
import { MAX_CONDICIONES, TIPOS_CONDICION, condicionVacia } from "../../logic/base";
import { CondicionFila } from "./CondicionFila";

interface Props {
  filtro: FiltroPublico;
  opciones: OpcionesBase;
  onChange: (f: FiltroPublico) => void;
  /** El error de FILTRO_INVALIDO (local o del backend): va arriba de las condiciones. */
  error?: string;
  disabled?: boolean;
}

export function ConstructorFiltro({ filtro, opciones, onChange, error, disabled = false }: Props) {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const condiciones = filtro.condiciones;

  useEffect(() => {
    if (!menu) return;
    const cerrar = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", cerrar);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", cerrar); document.removeEventListener("keydown", esc); };
  }, [menu]);

  const agregar = (tipo: TipoCondicion) => {
    setMenu(false);
    onChange({ condiciones: [...condiciones, condicionVacia(tipo)] });
  };
  const cambiar = (i: number, c: CondicionPublico) => onChange({ condiciones: condiciones.map((x, j) => (j === i ? c : x)) });
  const quitar = (i: number) => onChange({ condiciones: condiciones.filter((_, j) => j !== i) });

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Entran los contactos que cumplen <strong>todas</strong> las condiciones. Sin condiciones, entra toda la base.
      </p>
      {error && <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-700" role="alert">{error}</p>}
      {condiciones.length > 0 && (
        <ul className="space-y-2">
          {condiciones.map((c, i) => (
            <CondicionFila key={i} condicion={c} opciones={opciones} disabled={disabled} onChange={(n) => cambiar(i, n)} onQuitar={() => quitar(i)} />
          ))}
        </ul>
      )}
      {!disabled && (
        <div className="relative w-fit" ref={menuRef}>
          <button type="button" onClick={() => setMenu((m) => !m)} disabled={condiciones.length >= MAX_CONDICIONES}
            aria-haspopup="menu" aria-expanded={menu}
            className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
            <Plus size={14} /> Agregar condición
          </button>
          {menu && (
            <ul role="menu" className="absolute z-20 mt-1 w-56 rounded-xl border border-slate-100 bg-white shadow-lg py-1">
              {TIPOS_CONDICION.map((t) => (
                <li key={t.tipo} role="none">
                  <button type="button" role="menuitem" onClick={() => agregar(t.tipo)} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default ConstructorFiltro;
